import { DataTable } from '@/components/DataTable';
import {
  AuditLog,
  AuditLogBoolExp,
  AuditLogOrderBy,
  OrderBy,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import useDataTable from '@/hooks/useDataTable';
import { useSyncFiltersToSearchParams } from '@/hooks/useSyncFiltersToSearchParams';
import { SearchParam } from '@/models/search-param.models';
import { searchParamToTableSortingState } from '@/utils/search-param.function';
import { toSqlLikeString } from '@/utils/string';
import { useQuery } from '@tanstack/react-query';
import { PaginationState, SortingState } from '@tanstack/react-table';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { auditHistoryColumns } from '../components/AuditHistoryColumns';
import ListAuditHistoryHeader, {
  AuditHistoryFilter,
} from '../components/ListAuditLogHeader';
import { auditHistoryQuery } from '../graphql/query';

type AuditLogQueryFilterInput = {
  where: AuditLogBoolExp;
  limit: number;
  offset: number;
  orderBy: AuditLogOrderBy[];
};

export default function ListAuditHistoryPage() {
  const DEFAULT_PAGE_SIZE = 50;
  const DEFAULT_DATA_ORDER: AuditLogOrderBy[] = [
    {
      createdAt: OrderBy.Desc,
    },
  ];
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [filterFormsData, setFilterFormsData] = useState<AuditHistoryFilter>();

  const [filters, setFilters] = useState<AuditLogQueryFilterInput>({
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
    orderBy: DEFAULT_DATA_ORDER,
    where: {},
  });

  const [searchParams, _setSearchParams] = useSearchParams();
  const [sorting, setSorting] = useState<SortingState>([]);
  const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
  const [rowCount, setRowCount] = useState<number>(0);
  // This state indicate when if the search params are loaded after the page load.
  const [searchParamsLoaded, setSearchParamsLoaded] = useState<boolean>(false);

  const { data, isFetching, isLoading } = useQuery({
    queryKey: ['AuditHistory', filters],
    queryFn: () =>
      execute({
        query: auditHistoryQuery,
        variables: {
          limit: filters.limit,
          offset: filters.offset,
          where: filters.where,
          orderBy: filters.orderBy,
        },
      }),
    select: (data) => data,
    staleTime: 3000,
    enabled: searchParamsLoaded,
  });

  useEffect(() => {
    if (data) {
      setAuditLogs((data.auditLog ?? []) as AuditLog[]);
      setRowCount((data.auditLogAggregate?.aggregate?.count as number) ?? 0);
    }
  }, [data]);

  useSyncFiltersToSearchParams<AuditHistoryFilter>({
    filters: filterFormsData,
    pagination,
    sorting,
    setPagination,
    searchParamsLoaded,
    defaultPageSize: DEFAULT_PAGE_SIZE,
  });

  const table = useDataTable({
    data: auditLogs,
    columns: auditHistoryColumns,
    rowCount,
    sorting,
    pagination,
    setSorting,
    setPagination,
    manualFiltering: true,
    manualPagination: true,
    manualSorting: true,
  });

  useEffect(() => {
    const actor = searchParams.get(SearchParam.ACTOR);
    const action = searchParams.get(SearchParam.ACTION);

    const pageSize = parseInt(
      searchParams.get(SearchParam.PAGE_SIZE) ?? DEFAULT_PAGE_SIZE.toString(),
    );
    const pageIndex = parseInt(searchParams.get(SearchParam.PAGE) ?? '0');
    const orderBy = searchParams.get(SearchParam.ORDER_BY);
    const limit = pageSize;
    const offset = pageIndex * limit;
    const sortingState = searchParamToTableSortingState(orderBy);

    const actions = action ? action.split(',') : [];

    if (!searchParamsLoaded) {
      setFilterFormsData({
        actor,
        action: actions?.length > 0 ? actions : undefined,
      });
      setSorting(sortingState);
      setPagination({
        pageIndex,
        pageSize,
      });
    }

    // Create Site Where Filter from SearchParams.
    const andFilters: AuditLogBoolExp[] = [];
    if (actor) {
      andFilters.push({
        actorId: {
          _ilike: toSqlLikeString(actor),
        },
      });
    }

    if (actions.length > 0) {
      const orFilters: AuditLogBoolExp[] = [];
      actions.forEach((action) => {
        orFilters.push({
          action: {
            _ilike: toSqlLikeString(action),
          },
        });
      });
      andFilters.push({
        _or: orFilters,
      });
    }
    // Convert the react table SortingState to Site Query OrderBy
    const orderByFromParams = sortingState.map((sort): AuditLogOrderBy => {
      if (sort.id === 'actor') {
        return {
          aspNetUser: {
            userName: sort.desc ? OrderBy.DescNullsLast : OrderBy.AscNullsLast,
          },
        };
      }
      return {
        [sort.id]: sort.desc ? OrderBy.DescNullsLast : OrderBy.AscNullsLast,
      };
    });

    setSearchParamsLoaded(true);
    setFilters({
      limit,
      offset,
      where: {
        _and: andFilters,
      },
      orderBy:
        orderByFromParams?.length > 0 ? orderByFromParams : DEFAULT_DATA_ORDER,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  return (
    <>
      <ListAuditHistoryHeader
        table={table}
        filters={filterFormsData}
        setFilters={setFilterFormsData}
      />
      <DataTable table={table} loading={isLoading} fetching={isFetching} />
    </>
  );
}
